import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Layout from "../components/Layout";

const Updates = styled.ul`
  max-width: 900px;
  display: flex;
  flex-direction: column;
  list-style-type: none;
`;

const Update = styled.li`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 1rem;
  border: 1px solid var(--color-text);
  div {
    margin: 0.25rem;
    padding: 0.75rem;
  }
`;

const Label = styled.div`
  font-weight: bold;
`;

const Pill = styled.div`
  border-radius: 15px;
`;

const Available = styled(Pill)`
  background-color: var(--color-success);
  color: var(--color-background);
`;

const Unavailable = styled(Pill)`
  background-color: var(--color-error);
  color: var(--color-background);
`;

interface LogData {
  ID: number;
  Notes: string;
  Power: boolean;
  Internet: boolean;
  SMS: boolean;
  Timestamp: string;
}

const Log: React.FC<{ data: LogData }> = ({ data }) => {
  const isAvailable = (key: keyof Pick<LogData, 'Internet' | 'Power'>) => {
    const available = key in data && data[key] === true;
    return (
      <div>
        <Label>{key}</Label>
        {available ? (
          <Available>{key} Available</Available>
        ) : (
          <Unavailable>{key} Unavailable</Unavailable>
        )}
      </div>
    );
  };

  const keys: Array<keyof Pick<LogData, 'Internet' | 'Power'>> = ["Internet", "Power"];
  const timestamp = new Date(data.Timestamp);
  
  return (
    <Update>
      <div>
        <Label>Time</Label>
        <div>
          {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
        </div>
      </div>
      {keys.map((key) => isAvailable(key))}
      <div>
        <Label>Send method</Label>
        <div>
          {"SMS" in data && data.SMS !== null && data.SMS === true
            ? "SMS"
            : "Internet"}
        </div>
      </div>
      <div>
        <Label>Note</Label>
        <div>{data.Notes}</div>
      </div>
    </Update>
  );
};

const Hurricane: React.FC = () => {
  const [nodes, setNodes] = useState<LogData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const url = "https://api.airtable.com/v0/appUH2hjprVOUHRqf/Updates";
        const apiKey = import.meta.env.VITE_AIRTABLE_API_KEY;
        
        if (!apiKey) {
          throw new Error("Airtable API key not found");
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        const result = json.records.map((x: any) => x.fields);
        setNodes(result);
      } catch (error) {
        console.log("error", error);
        setError(error instanceof Error ? error.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (nodes.length === 0) return;

    const latestId = Math.max(...nodes.map(node => node.ID));
    
    const fetchUpdates = async () => {
      try {
        const url =
          "https://api.airtable.com/v0/appUH2hjprVOUHRqf/Updates?" +
          new URLSearchParams({
            filterByFormula: `ID > ${latestId}`,
          });

        const apiKey = import.meta.env.VITE_AIRTABLE_API_KEY;
        if (!apiKey) return;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (response.ok) {
          const json = await response.json();
          const result = json.records.map((x: any) => x.fields);
          if (result.length > 0) {
            setNodes((prevNodes) => [...result, ...prevNodes]);
          }
        }
      } catch (error) {
        console.log("error fetching updates", error);
      }
    };

    // Poll for updates every 5 minutes
    const interval = setInterval(fetchUpdates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [nodes]);

  if (loading) {
    return (
      <Layout pageTitle="Hurricane Updates">
        <div>Loading hurricane updates...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Hurricane Updates">
        <div>Error loading hurricane updates: {error}</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Hurricane Updates">
      <div>
        <h2>Status updates during **insert hurricane name here**</h2>
        <p>Automatic updates are sent every 3 hours (8, 11, 2, 5, etc.)</p>
        <p>
          In case of loss of power & internet, updates will be sent via SMS on
          an ad hoc basis.
        </p>
        <p>All times are EST.</p>
      </div>
      <Updates>
        {nodes
          .sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime())
          .map((x) => (
            <Log key={x.ID} data={x} />
          ))}
      </Updates>
    </Layout>
  );
};

export default Hurricane;